/**
 * Shop pages — grid, product detail, cart, checkout, order status.
 * Uses existing api client and reuses payment infrastructure (Stripe/PayPal/Bonifico).
 * Layout: ShopHeader + main content + minimal footer (isolates from main site chrome).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errMsg, API_BASE } from "@/lib/config";
import { useCart } from "@/lib/cart";
import Seo from "@/lib/Seo";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  ShoppingCart, ArrowLeft, ArrowRight, Trash2, Minus, Plus, Package,
  CreditCard, Landmark, Loader2, Check, Copy, Truck, Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const fullMediaUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/")) return `${API_BASE}${u}`;
  return u;
};
const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

/** Fixed shop categories — order matters (rendered as filter chips). */
export const SHOP_CATEGORIES = [
  { id: "natale",  label: "Natale" },
  { id: "pasqua",  label: "Pasqua" },
  { id: "cucina",  label: "Oggetti Cucina" },
  { id: "altri",   label: "Altri Oggetti" },
];
const CAT_LABEL = Object.fromEntries(SHOP_CATEGORIES.map((c) => [c.id, c.label]));

/* ══════════════════════════════ Shop chrome ══════════════════════════════ */
export function ShopHeader() {
  const { count } = useCart();
  return (
    <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/85 backdrop-blur-xl" data-testid="shop-header">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/shop" className="flex items-center gap-3">
          <img src="/assets/logo/gefion.png" alt="Gefion" className="h-8 w-auto object-contain brightness-0 invert" />
          <span className="font-mono-tech text-[10px] text-white/50 uppercase tracking-wider">Shop · Oggetti in Inox</span>
        </Link>
        <nav className="flex items-center gap-6 font-mono-tech text-[11px]">
          <Link to="/" className="text-white/50 hover:text-white uppercase">← Sito Principale</Link>
          <Link to="/shop/carrello" className="relative text-white hover:text-[#ff4b00]" data-testid="shop-cart-link">
            <ShoppingCart size={18} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#ff4b00] text-black w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center" data-testid="shop-cart-count">{count}</span>
            )}
          </Link>
        </nav>
      </div>
    </header>
  );
}

export function ShopFooter() {
  return (
    <footer className="border-t border-[#141414] mt-24 py-10 text-center font-mono-tech text-[10px] text-white/40">
      <div>Gefion S.r.l. · Via 1° Maggio 6, 33070 Budoia (PN) · P.IVA 01675990939</div>
      <div className="mt-2 space-x-2">
        <Link to="/shop/spedizioni-resi" className="text-[#ff4b00] hover:underline" data-testid="shop-footer-policy">Spedizioni &amp; Resi</Link>
        <span className="text-white/20">·</span>
        <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy</Link>
        <span className="text-white/20">·</span>
        <Link to="/termini" className="text-[#ff4b00] hover:underline">Termini</Link>
      </div>
      <div className="mt-2">Realizzazioni artigianali con acciai inox 304 e 316</div>
    </footer>
  );
}

const ShopShell = ({ children }) => (
  <div className="min-h-screen bg-black text-white">
    <ShopHeader />
    <main>{children}</main>
    <ShopFooter />
  </div>
);

/* ══════════════════════════════ Product listing ══════════════════════════════ */
export function ShopHome() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get(`/shop/products${cat ? `?category=${cat}` : ""}`)
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [cat]);

  const categories = SHOP_CATEGORIES;

  return (
    <ShopShell>
      <Seo
        title="Shop · Oggetti artigianali in acciaio inox"
        description="Piccole serie di oggetti in acciaio inox 304 e 316, realizzati artigianalmente nella nostra officina di Budoia (PN). Palline di Natale, decorazioni, oggetti per la cucina."
        path="/shop"
        keywords="oggetti inox artigianali, palline natale acciaio, decorazioni inox, taglio laser Pordenone, gefion shop"
      />
      <section className="max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles size={14} className="text-[#ff4b00]" />
          <span className="font-mono-tech text-[10px] uppercase text-white/60 tracking-widest">Officina · Collezione limitata</span>
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-4xl" data-testid="shop-hero-title">
          L&apos;acciaio<br />
          <em className="font-serif not-italic text-[#ff4b00]">diventa oggetto.</em>
        </h1>
        <p className="mt-6 text-white/60 max-w-xl leading-relaxed">
          Ogni pezzo esce dalla nostra officina laser. Acciaio inox 304 e 316,
          finiture spazzolate o lucidate. Piccole serie curate nel dettaglio.
        </p>
      </section>

      <div className="max-w-7xl mx-auto px-6 mb-8 flex flex-wrap gap-2">
        <button onClick={() => setCat("")} data-testid="shop-cat-all"
          className={`px-4 py-2 border font-mono-tech text-[11px] uppercase transition-colors ${cat === "" ? "border-[#ff4b00] text-[#ff4b00]" : "border-[#1a1a1a] text-white/60 hover:text-white"}`}>Tutto</button>
        {categories.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)} data-testid={`shop-cat-${c.id}`}
            className={`px-4 py-2 border font-mono-tech text-[11px] uppercase transition-colors ${cat === c.id ? "border-[#ff4b00] text-[#ff4b00]" : "border-[#1a1a1a] text-white/60 hover:text-white"}`}>{c.label}</button>
        ))}
      </div>

      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="shop-grid">
        {loading ? (
          <div className="text-white/40 font-mono-tech text-xs">Caricamento…</div>
        ) : products.length === 0 ? (
          <div className="border border-dashed border-[#333] p-16 text-center text-white/60">
            <Package size={40} className="mx-auto mb-4 text-white/30" />
            <div className="font-display text-2xl mb-2">Presto disponibili</div>
            <div className="font-mono-tech text-xs">Stiamo preparando la prima collezione. Torna a trovarci.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => {
              const minVariant = (p.variants || []).reduce((m, v) => !m || v.price_eur < m.price_eur ? v : m, null);
              const price = minVariant?.price_eur ?? p.base_price_eur;
              const hasStock = (p.variants || []).some((v) => v.in_stock);
              return (
                <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`shop-product-${p.slug}`}
                  className="group block border border-[#1a1a1a] hover:border-[#ff4b00] transition-all">
                  <div className="aspect-square bg-[#0a0a0a] relative overflow-hidden">
                    {p.images[0] ? (
                      <img src={fullMediaUrl(p.images[0])} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20"><Package size={48} /></div>
                    )}
                    {!hasStock && (
                      <div className="absolute top-3 left-3 bg-black/80 px-2 py-1 font-mono-tech text-[9px] uppercase text-white/70">Esaurito</div>
                    )}
                  </div>
                  <div className="p-5 border-t border-[#1a1a1a]">
                    <div className="font-mono-tech text-[10px] uppercase text-white/40 mb-1">{CAT_LABEL[p.category] || p.category}</div>
                    <div className="font-display text-xl mb-3">{p.name}</div>
                    <div className="flex items-end justify-between">
                      <div className="font-display text-2xl text-[#ff4b00]">{fmtEur(price)}</div>
                      <div className="font-mono-tech text-[10px] text-white/40">{p.variants.length} variant{p.variants.length !== 1 ? "i" : "e"}</div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </ShopShell>
  );
}

/* ══════════════════════════════ Product detail ══════════════════════════════ */
export function ShopProduct() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selVar, setSelVar] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/shop/products/${slug}`)
      .then((r) => {
        setP(r.data);
        const firstInStock = (r.data.variants || []).find((v) => v.in_stock) || r.data.variants[0];
        setSelVar(firstInStock);
      })
      .catch(() => setP(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <ShopShell><div className="max-w-7xl mx-auto px-6 py-24 text-white/40 font-mono-tech text-xs">Caricamento…</div></ShopShell>;
  if (!p) return <ShopShell><div className="max-w-7xl mx-auto px-6 py-24 text-white/60">Prodotto non trovato. <Link to="/shop" className="text-[#ff4b00]">Torna al catalogo</Link></div></ShopShell>;

  const canAdd = selVar && selVar.in_stock && qty > 0 && qty <= selVar.stock;
  const addNow = () => {
    if (!canAdd) return;
    add({
      variant_id: selVar.id,
      product_id: p.id, product_slug: p.slug, product_name: p.name,
      size_label: selVar.size_label,
      price_eur: selVar.price_eur, weight_g: selVar.weight_g,
      image: p.images[0], qty,
    });
    toast.success("Aggiunto al carrello");
  };
  const buyNow = () => { addNow(); if (canAdd) nav("/shop/carrello"); };

  const productJsonLd = p ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description,
    image: p.images.map((im) => im.startsWith("http") ? im : `https://www.gefion-evolution.it${im.startsWith("/api/") ? im : "/api/media/" + im}`),
    brand: { "@type": "Brand", name: "Gefion Evolution" },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: Math.min(...p.variants.map((v) => v.price_eur)),
      highPrice: Math.max(...p.variants.map((v) => v.price_eur)),
      offerCount: p.variants.length,
      availability: p.variants.some((v) => v.in_stock) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
  } : null;

  return (
    <ShopShell>
      {p && (
        <Seo
          title={p.name}
          description={p.description || `${p.name} — oggetto artigianale in acciaio inox realizzato a Budoia (PN).`}
          path={`/shop/${p.slug}`}
          image={p.images?.[0] ? (p.images[0].startsWith("http") ? p.images[0] : `https://www.gefion-evolution.it${p.images[0]}`) : undefined}
          type="product"
          jsonLd={productJsonLd}
        />
      )}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Link to="/shop" className="font-mono-tech text-[11px] text-white/50 hover:text-white flex items-center gap-2 mb-8"><ArrowLeft size={12} /> Torna al catalogo</Link>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-testid="shop-product-detail">
          <div>
            <div className="aspect-square bg-[#0a0a0a] border border-[#1a1a1a]">
              {p.images[imgIdx] ? (
                <img src={fullMediaUrl(p.images[imgIdx])} alt={p.name} className="w-full h-full object-contain" data-testid="shop-product-main-img" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20"><Package size={60} /></div>
              )}
            </div>
            {p.images.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {p.images.map((im, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className={`aspect-square border ${i === imgIdx ? "border-[#ff4b00]" : "border-[#1a1a1a]"}`}>
                    <img src={fullMediaUrl(im)} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="font-mono-tech text-[10px] uppercase text-white/40 mb-2">{CAT_LABEL[p.category] || p.category}</div>
            <h1 className="font-display text-4xl md:text-5xl mb-4" data-testid="shop-product-name">{p.name}</h1>
            <div className="font-display text-3xl text-[#ff4b00] mb-6" data-testid="shop-product-price">
              {fmtEur(selVar?.price_eur ?? p.base_price_eur)}
              <span className="font-mono-tech text-[10px] text-white/40 ml-2">IVA inclusa</span>
            </div>
            <p className="text-white/70 leading-relaxed mb-8 whitespace-pre-wrap">{p.description}</p>

            {p.variants.length > 0 && (
              <div className="mb-6">
                <div className="font-mono-tech text-[10px] uppercase text-white/50 mb-3">Variante</div>
                <div className="flex flex-wrap gap-2">
                  {p.variants.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setSelVar(v)}
                      disabled={!v.in_stock}
                      data-testid={`shop-variant-${v.id}`}
                      className={`px-4 py-2 border font-mono-tech text-xs transition-colors ${
                        selVar?.id === v.id ? "border-[#ff4b00] text-[#ff4b00]"
                        : v.in_stock ? "border-[#1a1a1a] hover:border-white/40"
                        : "border-[#1a1a1a] text-white/30 line-through cursor-not-allowed"
                      }`}
                    >
                      {v.size_label}
                    </button>
                  ))}
                </div>
                {selVar && selVar.dimensions_note && (
                  <div className="font-mono-tech text-[10px] text-white/50 mt-3">{selVar.dimensions_note} · peso {selVar.weight_g}g</div>
                )}
              </div>
            )}

            <div className="mb-6">
              <div className="font-mono-tech text-[10px] uppercase text-white/50 mb-3">Quantità</div>
              <div className="flex items-center border border-[#1a1a1a] w-fit">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-white/5" data-testid="shop-qty-dec"><Minus size={14} /></button>
                <input type="number" min="1" max={selVar?.stock || 1} value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(selVar?.stock || 1, parseInt(e.target.value) || 1)))}
                  data-testid="shop-qty-input"
                  className="w-16 bg-transparent text-center py-2 border-l border-r border-[#1a1a1a] focus:outline-none" />
                <button onClick={() => setQty(Math.min(selVar?.stock || 1, qty + 1))} className="px-3 py-2 hover:bg-white/5" data-testid="shop-qty-inc"><Plus size={14} /></button>
              </div>
              {selVar && <div className="font-mono-tech text-[10px] text-white/40 mt-2">{selVar.stock} disponibili</div>}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={addNow} disabled={!canAdd} data-testid="shop-add-to-cart"
                className="btn-ghost flex-1 py-3 disabled:opacity-40 disabled:cursor-not-allowed">
                <ShoppingCart size={14} /> Aggiungi al carrello
              </button>
              <button onClick={buyNow} disabled={!canAdd} data-testid="shop-buy-now"
                className="btn-primary flex-1 py-3 disabled:opacity-40 disabled:cursor-not-allowed">
                Compra ora <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </ShopShell>
  );
}

/* ══════════════════════════════ Cart page ══════════════════════════════ */
export function ShopCart() {
  const { items, setQty, remove, subtotal, totalWeightG } = useCart();
  const [ship, setShip] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!items.length) { setShip(null); return; }
    setLoading(true);
    api.post("/shop/shipping-quote", { cart: items.map(({ variant_id, qty }) => ({ variant_id, qty })) })
      .then((r) => setShip(r.data))
      .catch((e) => toast.error(errMsg(e, "Errore preventivo spedizione")))
      .finally(() => setLoading(false));
  }, [items]);

  if (!items.length) return (
    <ShopShell>
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="shop-cart-empty">
        <ShoppingCart size={40} className="mx-auto mb-6 text-white/20" />
        <div className="font-display text-3xl mb-3">Carrello vuoto</div>
        <div className="text-white/50 mb-8">Scopri gli oggetti unici della nostra officina.</div>
        <Link to="/shop" className="btn-primary inline-flex">Vai al catalogo <ArrowRight size={14} /></Link>
      </div>
    </ShopShell>
  );

  return (
    <ShopShell>
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl md:text-5xl mb-10" data-testid="shop-cart-title">Il tuo carrello</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-3">
            {items.map((it) => (
              <div key={it.variant_id} className="flex gap-4 border border-[#1a1a1a] p-4" data-testid={`cart-item-${it.variant_id}`}>
                <div className="w-20 h-20 bg-[#0a0a0a] flex-shrink-0">
                  {it.image ? <img src={fullMediaUrl(it.image)} alt="" className="w-full h-full object-contain" /> : <Package className="m-auto mt-6 text-white/20" />}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/shop/${it.product_slug}`} className="font-display text-lg hover:text-[#ff4b00]">{it.product_name}</Link>
                  <div className="font-mono-tech text-[10px] text-white/50">{it.size_label} · {it.weight_g}g cad.</div>
                  <div className="mt-3 flex items-center gap-4">
                    <div className="flex items-center border border-[#1a1a1a]">
                      <button onClick={() => setQty(it.variant_id, it.qty - 1)} className="px-2 py-1 hover:bg-white/5"><Minus size={12} /></button>
                      <span className="px-3 py-1 font-mono-tech text-xs">{it.qty}</span>
                      <button onClick={() => setQty(it.variant_id, it.qty + 1)} className="px-2 py-1 hover:bg-white/5"><Plus size={12} /></button>
                    </div>
                    <button onClick={() => remove(it.variant_id)} data-testid={`cart-remove-${it.variant_id}`} className="text-white/40 hover:text-red-400 text-[10px] font-mono-tech flex items-center gap-1">
                      <Trash2 size={12} /> rimuovi
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg">{fmtEur(it.price_eur * it.qty)}</div>
                  <div className="font-mono-tech text-[10px] text-white/40">{fmtEur(it.price_eur)}/cad</div>
                </div>
              </div>
            ))}
          </div>
          <div className="border border-[#ff4b00]/60 bg-[#0a0a0a] p-6 h-fit sticky top-24" data-testid="cart-summary">
            <div className="font-mono-tech text-[10px] text-[#ff4b00] uppercase mb-4">Riepilogo</div>
            <div className="space-y-3 font-mono-tech text-xs">
              <div className="flex justify-between"><span className="text-white/50">Subtotale</span><span>{fmtEur(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-white/50">Peso totale</span><span>{(totalWeightG / 1000).toFixed(2)} kg</span></div>
              {loading ? (
                <div className="flex justify-between"><span className="text-white/50">Spedizione</span><Loader2 size={12} className="animate-spin" /></div>
              ) : ship ? (
                <div className="flex justify-between"><span className="text-white/50">Spedizione ({ship.shipping.label})</span><span>{fmtEur(ship.shipping.cost)}</span></div>
              ) : null}
              <div className="border-t border-[#1a1a1a] pt-3 flex justify-between items-baseline">
                <span className="font-display text-lg text-white">Totale</span>
                <span className="font-display text-2xl text-[#ff4b00]" data-testid="cart-total">{fmtEur((ship?.total) ?? subtotal)}</span>
              </div>
            </div>
            <Link to="/shop/checkout" data-testid="cart-checkout-btn" className="btn-primary w-full mt-6 justify-center">
              Procedi al checkout <ArrowRight size={14} />
            </Link>
            <div className="mt-4 font-mono-tech text-[9px] text-white/40">IVA inclusa. Spedizione tramite corriere standard.</div>
          </div>
        </div>
      </div>
    </ShopShell>
  );
}

/* ══════════════════════════════ Checkout ══════════════════════════════ */
export function ShopCheckout() {
  const { items, subtotal, totalWeightG, clear } = useCart();
  const [methods, setMethods] = useState(null);
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "", company: "",
    address_line: "", city: "", postal_code: "", province: "", country: "IT", notes: "",
  });
  const [method, setMethod] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ship, setShip] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/payments/methods").then((r) => setMethods(r.data)).catch(() => setMethods({}));
    if (items.length) {
      api.post("/shop/shipping-quote", { cart: items.map(({ variant_id, qty }) => ({ variant_id, qty })) })
        .then((r) => setShip(r.data));
    }
  }, [items]);

  if (!items.length) return <ShopShell><div className="max-w-3xl mx-auto px-6 py-24 text-center"><div className="font-display text-2xl mb-4">Carrello vuoto</div><Link to="/shop" className="btn-primary inline-flex">Al catalogo</Link></div></ShopShell>;

  const patch = (k, v) => setCustomer({ ...customer, [k]: v });
  const total = ship?.total ?? subtotal;
  const canSubmit = customer.name && customer.email && customer.address_line && customer.city && customer.postal_code && customer.province && consent && method;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.post("/shop/orders", {
        cart: items.map(({ variant_id, qty }) => ({ variant_id, qty })),
        customer, consent_privacy: consent, payment_method: method,
      });
      if (method === "stripe" && r.data.checkout_url) {
        clear();
        window.location.href = r.data.checkout_url;
      } else {
        // For paypal/bonifico → send to order page, keep cart until payment confirmed
        if (method === "bonifico") clear();
        nav(`/shop/ordine/${r.data.order_id}`);
      }
    } catch (err) {
      toast.error(errMsg(err, "Errore creazione ordine"));
      setSubmitting(false);
    }
  };

  return (
    <ShopShell>
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/shop/carrello" className="font-mono-tech text-[11px] text-white/50 hover:text-white flex items-center gap-2 mb-6"><ArrowLeft size={12} /> Torna al carrello</Link>
        <h1 className="font-display text-4xl md:text-5xl mb-10" data-testid="shop-checkout-title">Checkout</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6" data-testid="shop-checkout-form">
            <section className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
              <div className="font-mono-tech text-[10px] uppercase text-white/50 mb-4">Contatti</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="field-label">Nome e cognome *</label><input className="field-boxed mt-2" value={customer.name} onChange={(e) => patch("name", e.target.value)} data-testid="shop-checkout-name" /></div>
                <div><label className="field-label">Email *</label><input type="email" className="field-boxed mt-2" value={customer.email} onChange={(e) => patch("email", e.target.value)} data-testid="shop-checkout-email" /></div>
                <div><label className="field-label">Telefono</label><input className="field-boxed mt-2" value={customer.phone} onChange={(e) => patch("phone", e.target.value)} /></div>
                <div><label className="field-label">Azienda (opzionale)</label><input className="field-boxed mt-2" value={customer.company} onChange={(e) => patch("company", e.target.value)} /></div>
              </div>
            </section>
            <section className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
              <div className="font-mono-tech text-[10px] uppercase text-white/50 mb-4">Spedizione</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><label className="field-label">Indirizzo *</label><input className="field-boxed mt-2" value={customer.address_line} onChange={(e) => patch("address_line", e.target.value)} data-testid="shop-checkout-address" /></div>
                <div><label className="field-label">Città *</label><input className="field-boxed mt-2" value={customer.city} onChange={(e) => patch("city", e.target.value)} data-testid="shop-checkout-city" /></div>
                <div><label className="field-label">CAP *</label><input className="field-boxed mt-2" value={customer.postal_code} onChange={(e) => patch("postal_code", e.target.value)} data-testid="shop-checkout-cap" /></div>
                <div><label className="field-label">Provincia *</label><input maxLength={2} className="field-boxed mt-2 uppercase" value={customer.province} onChange={(e) => patch("province", e.target.value.toUpperCase())} data-testid="shop-checkout-province" /></div>
                <div><label className="field-label">Paese</label><input className="field-boxed mt-2" value={customer.country} onChange={(e) => patch("country", e.target.value.toUpperCase())} /></div>
                <div className="md:col-span-2"><label className="field-label">Note (opzionale)</label><textarea className="field-boxed mt-2 h-20" value={customer.notes} onChange={(e) => patch("notes", e.target.value)} /></div>
              </div>
            </section>
            <section className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
              <div className="font-mono-tech text-[10px] uppercase text-white/50 mb-4">Metodo di pagamento</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {methods?.stripe?.enabled && (
                  <button onClick={() => setMethod("stripe")} data-testid="shop-method-stripe"
                    className={`p-4 border text-left transition-colors ${method === "stripe" ? "border-[#ff4b00] bg-black" : "border-[#1a1a1a] hover:border-white/30"}`}>
                    <CreditCard size={16} className="text-[#ff4b00] mb-2" />
                    <div className="font-display text-base">Carta</div>
                    <div className="font-mono-tech text-[10px] text-white/50">Stripe · sicuro</div>
                  </button>
                )}
                {methods?.paypal?.enabled && (
                  <button onClick={() => setMethod("paypal")} data-testid="shop-method-paypal"
                    className={`p-4 border text-left ${method === "paypal" ? "border-[#ff4b00] bg-black" : "border-[#1a1a1a] hover:border-white/30"}`}>
                    <div className="mb-2"><span className="font-display text-base text-[#0070ba]">Pay</span><span className="font-display text-base text-[#003087]">Pal</span></div>
                    <div className="font-mono-tech text-[10px] text-white/50">Immediato</div>
                  </button>
                )}
                {methods?.bonifico?.enabled && (
                  <button onClick={() => setMethod("bonifico")} data-testid="shop-method-bonifico"
                    className={`p-4 border text-left ${method === "bonifico" ? "border-[#ff4b00] bg-black" : "border-[#1a1a1a] hover:border-white/30"}`}>
                    <Landmark size={16} className="text-[#ff4b00] mb-2" />
                    <div className="font-display text-base">Bonifico</div>
                    <div className="font-mono-tech text-[10px] text-white/50">1-2 giorni</div>
                  </button>
                )}
              </div>
            </section>
            <label className="flex items-start gap-3 font-mono-tech text-[11px] text-white/70 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#ff4b00]" data-testid="shop-checkout-consent" />
              <span>Ho letto e accetto la <Link to="/privacy" className="text-[#ff4b00] hover:underline">Privacy Policy</Link> e le <Link to="/shop/spedizioni-resi" className="text-[#ff4b00] hover:underline">condizioni di vendita (spedizioni &amp; resi)</Link>. Autorizzo il trattamento dei miei dati per l&apos;esecuzione dell&apos;ordine. *</span>
            </label>
          </div>
          <div className="border border-[#ff4b00]/60 bg-[#0a0a0a] p-6 h-fit sticky top-24">
            <div className="font-mono-tech text-[10px] text-[#ff4b00] uppercase mb-4">Riepilogo ordine</div>
            <div className="space-y-2 font-mono-tech text-xs mb-4">
              {items.map((it) => (
                <div key={it.variant_id} className="flex justify-between">
                  <span className="text-white/70 truncate">{it.product_name} · {it.size_label} × {it.qty}</span>
                  <span className="ml-2">{fmtEur(it.price_eur * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#1a1a1a] pt-3 space-y-2 font-mono-tech text-xs">
              <div className="flex justify-between"><span className="text-white/50">Peso</span><span>{(totalWeightG / 1000).toFixed(2)} kg</span></div>
              {ship && <div className="flex justify-between"><span className="text-white/50">Spedizione</span><span>{fmtEur(ship.shipping.cost)}</span></div>}
              <div className="border-t border-[#1a1a1a] pt-2 flex justify-between items-baseline">
                <span className="font-display text-base">Totale</span>
                <span className="font-display text-2xl text-[#ff4b00]" data-testid="shop-checkout-total">{fmtEur(total)}</span>
              </div>
            </div>
            <button onClick={submit} disabled={!canSubmit || submitting} data-testid="shop-checkout-submit"
              className="btn-primary w-full mt-6 justify-center disabled:opacity-40">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Conferma ordine <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
      </div>
    </ShopShell>
  );
}

/* ══════════════════════════════ Order status ══════════════════════════════ */
export function ShopOrder() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [captured, setCaptured] = useState(false);

  const load = () => api.get(`/shop/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  if (loading) return <ShopShell><div className="max-w-3xl mx-auto px-6 py-24 text-white/40 font-mono-tech">Caricamento…</div></ShopShell>;
  if (!order) return <ShopShell><div className="max-w-3xl mx-auto px-6 py-24 text-white/60">Ordine non trovato.</div></ShopShell>;

  const paidHere = params.get("paid") === "1";
  const STATUS_LABEL = {
    pending_payment: { l: "In attesa di pagamento", c: "text-[#ff4b00]" },
    awaiting_bank_transfer: { l: "In attesa del bonifico", c: "text-[#ff4b00]" },
    paid: { l: "Pagato · in preparazione", c: "text-green-400" },
    preparing: { l: "In preparazione", c: "text-green-400" },
    shipped: { l: "Spedito", c: "text-blue-400" },
    delivered: { l: "Consegnato", c: "text-white" },
    canceled: { l: "Annullato", c: "text-white/40" },
  };
  const S = STATUS_LABEL[order.status] || { l: order.status, c: "text-white" };
  const isBonifico = order.payment?.method === "bonifico";
  const isPending = order.status === "pending_payment" || order.status === "awaiting_bank_transfer";

  return (
    <ShopShell>
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="shop-order-page">
        <div className="font-mono-tech text-[10px] uppercase text-white/40 mb-2">Ordine #{order.id.slice(0, 8).toUpperCase()}</div>
        <h1 className="font-display text-4xl mb-4">Grazie{order.customer?.name ? `, ${order.customer.name}` : ""}!</h1>
        <div className={`font-display text-2xl ${S.c} mb-10`} data-testid="shop-order-status">◐ {S.l}</div>

        {paidHere && order.status === "paid" && (
          <div className="border border-green-400/40 bg-green-400/5 p-6 mb-8">
            <div className="font-display text-xl mb-2 flex items-center gap-2"><Check size={20} className="text-green-400" /> Pagamento ricevuto</div>
            <div className="text-white/70 text-sm">Ti abbiamo inviato un&apos;email di conferma. Prepariamo il tuo pacco.</div>
          </div>
        )}

        {isBonifico && order.payment?.bonifico && (
          <div className="border border-[#ff4b00]/60 bg-[#ff4b00]/5 p-6 mb-8" data-testid="shop-order-bonifico">
            <div className="font-display text-2xl mb-1">Coordinate bonifico</div>
            <div className="font-mono-tech text-[10px] text-white/50 mb-6">Effettua il bonifico entro 7 giorni. Riceverai email quando confermeremo il pagamento.</div>
            <BonificoRow label="Beneficiario" value={order.payment.bonifico.holder} />
            <BonificoRow label="Banca" value={order.payment.bonifico.bank} />
            <BonificoRow label="IBAN" value={order.payment.bonifico.iban} />
            {order.payment.bonifico.bic && <BonificoRow label="BIC" value={order.payment.bonifico.bic} />}
            <BonificoRow label="Causale" value={order.payment.bonifico.causale} />
            <div className="mt-6 flex justify-between items-baseline pt-4 border-t border-[#1a1a1a]">
              <span className="font-display text-lg">Totale</span>
              <span className="font-display text-3xl text-[#ff4b00]">{fmtEur(order.total_eur)}</span>
            </div>
          </div>
        )}

        {order.payment?.method === "paypal" && isPending && !captured && (
          <ShopOrderPayPalGate order={order} onCaptured={() => { setCaptured(true); load(); }} />
        )}

        <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
          <div className="font-mono-tech text-[10px] uppercase text-white/50 mb-4">Riepilogo</div>
          {order.items.map((it) => (
            <div key={it.variant_id} className="flex justify-between py-2 border-b border-[#141414] font-mono-tech text-xs">
              <span className="text-white/80">{it.qty} × {it.product_name} · {it.size_label}</span>
              <span>{fmtEur(it.line_total_eur)}</span>
            </div>
          ))}
          <div className="flex justify-between py-2 font-mono-tech text-xs">
            <span className="text-white/50">Spedizione ({order.shipping_label})</span>
            <span>{fmtEur(order.shipping_cost_eur)}</span>
          </div>
          <div className="border-t border-[#1a1a1a] pt-3 mt-2 flex justify-between items-baseline">
            <span className="font-display text-lg">Totale</span>
            <span className="font-display text-2xl text-[#ff4b00]">{fmtEur(order.total_eur)}</span>
          </div>
        </div>
        <Link to="/shop" className="mt-10 inline-flex btn-ghost">← Continua a fare acquisti</Link>
      </div>
    </ShopShell>
  );
}

function BonificoRow({ label, value }) {
  const [ok, setOk] = useState(false);
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#141414]">
      <span className="text-white/50 font-mono-tech text-[10px] uppercase">{label}</span>
      <div className="flex items-center">
        <span className="font-mono-tech text-sm select-all">{value}</span>
        <button
          onClick={() => { navigator.clipboard?.writeText(value); setOk(true); setTimeout(() => setOk(false), 1500); }}
          className="ml-3 text-white/50 hover:text-[#ff4b00]" title="Copia"
        >
          {ok ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}

/** Small inline PayPal button on the order page, when the user chose PayPal at checkout. */
function ShopOrderPayPalGate({ order, onCaptured }) {
  const [meta, setMeta] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/payments/methods").then((r) => setMeta(r.data.paypal));
  }, []);

  if (!meta?.enabled || !meta?.client_id) return null;

  return (
    <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a] mb-8" data-testid="shop-order-paypal">
      <div className="font-display text-xl mb-4">Completa il pagamento con PayPal · {fmtEur(order.total_eur)}</div>
      <PayPalScriptProvider options={{ clientId: meta.client_id, currency: "EUR", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
          disabled={creating}
          createOrder={() => {
            // paypal_order_id already generated at order creation
            return order.payment?.paypal_order_id ? Promise.resolve(order.payment.paypal_order_id) : Promise.reject(new Error("Nessun PayPal order id"));
          }}
          onApprove={async (data) => {
            setCreating(true);
            try {
              const r = await api.post(`/shop/orders/${order.id}/paypal-capture`, { paypal_order_id: data.orderID });
              if (r.data.ok) { toast.success("Pagamento completato"); onCaptured(); }
              else toast.error(`Stato: ${r.data.status}`);
            } catch (e) { toast.error(errMsg(e, "Errore cattura pagamento")); }
            finally { setCreating(false); }
          }}
          onError={() => toast.error("Errore PayPal — riprova")}
        />
      </PayPalScriptProvider>
    </div>
  );
}

/* ══════════════════════════════ Layout wrapper for /shop routes ══════════════════════════════ */
export function ShopBadgeOnMainSite() {
  return (
    <Link to="/shop" data-testid="shop-badge-main"
      className="fixed bottom-6 right-6 z-40 bg-[#ff4b00] text-black px-4 py-3 font-mono-tech text-[11px] uppercase shadow-2xl hover:scale-105 transition-transform flex items-center gap-2">
      <Sparkles size={14} /> Shop Inox
    </Link>
  );
}
